import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Tabs } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { H5DetailBackToListContext } from "@components/H5DetailBackToList";
import "./style.css";

const { TabPane } = Tabs;

const flattenChildren = (children) => {
  const result = [];
  React.Children.forEach(children, (child) => {
    if (!child) return;
    if (React.isValidElement(child) && child.type === React.Fragment) {
      result.push(...flattenChildren(child.props.children));
      return;
    }
    result.push(child);
  });
  return result;
};

const getNodeText = (node) => {
  if (node === undefined || node === null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join("");
  if (React.isValidElement(node)) return getNodeText(node.props?.children);
  return String(node);
};

const getExtraNode = (extra) => {
  if (!extra) return null;
  if (React.isValidElement(extra) || typeof extra === "string" || typeof extra === "number") {
    return extra;
  }
  if (extra.left || extra.right) {
    return (
      <>
        {extra.left}
        {extra.right}
      </>
    );
  }
  return null;
};

const useIsH5 = () => {
  const reduxIsPC = useSelector((state) => state.App?.isPC);
  const getViewportIsH5 = () =>
    typeof window !== "undefined" ? window.innerWidth <= 767 : reduxIsPC === false;
  const [isH5, setIsH5] = useState(() => reduxIsPC === false || getViewportIsH5());

  useEffect(() => {
    const onResize = () => setIsH5(reduxIsPC === false || getViewportIsH5());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [reduxIsPC]);

  return isH5;
};

const DetailTabs = (props) => {
  const {
    children,
    activeKey,
    defaultActiveKey,
    items,
    onChange,
    tabBarExtraContent,
    mobileThreshold = 2,
    mobileTitle = "Select Section",
    className = "",
    ...rest
  } = props;
  const isH5 = useIsH5();
  const { backButton, registerDetailTabs } = useContext(H5DetailBackToListContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [innerActiveKey, setInnerActiveKey] = useState(defaultActiveKey);
  const childList = useMemo(() => flattenChildren(children), [children]);
  const sections = useMemo(
    () => {
      if (items?.length) {
        return items
          .filter((item) => item && item.key)
          .map((item) => ({
            key: String(item.key),
            label: getNodeText(item.label) || String(item.key),
            tab: item.label,
          }));
      }

      return childList
        .filter((child) => React.isValidElement(child))
        .map((child) => ({
          key: String(child.key ?? child.props?.tabKey ?? ""),
          label: getNodeText(child.props?.tab) || String(child.key ?? ""),
          tab: child.props?.tab,
        }))
        .filter((item) => item.key);
    },
    [childList, items]
  );
  const shouldUseMobileSelector = isH5 && sections.length > mobileThreshold;
  const currentKey = String(activeKey ?? innerActiveKey ?? defaultActiveKey ?? sections[0]?.key ?? "");
  const activeSection = sections.find((item) => item.key === currentKey) || sections[0];
  const mobileExtra = getExtraNode(tabBarExtraContent);

  useEffect(() => {
    if (!shouldUseMobileSelector || !registerDetailTabs) return undefined;
    return registerDetailTabs();
  }, [registerDetailTabs, shouldUseMobileSelector]);

  const handleChange = (key) => {
    setInnerActiveKey(key);
    onChange?.(key);
    setDrawerOpen(false);
  };

  if (!shouldUseMobileSelector) {
    return (
      <Tabs
        {...rest}
        activeKey={activeKey}
        defaultActiveKey={defaultActiveKey}
        items={items}
        onChange={onChange}
        tabBarExtraContent={tabBarExtraContent}
        className={className}
      >
        {children}
      </Tabs>
    );
  }

  return (
    <div className={`detail-tabs detail-tabs-mobile ${className}`}>
      <div className="detail-tabs-mobile-header">
        <div className={`detail-tabs-mobile-main-row${backButton ? "" : " detail-tabs-mobile-main-row-single"}`}>
          {backButton ? <div className="detail-tabs-mobile-back">{backButton}</div> : null}
          <Button
            className="detail-tabs-mobile-trigger"
            onClick={() => setDrawerOpen(true)}
          >
            <span className="detail-tabs-mobile-trigger-label">
              {activeSection?.tab || activeSection?.label}
            </span>
            <DownOutlined className="detail-tabs-mobile-trigger-icon" />
          </Button>
        </div>
        {mobileExtra ? (
          <div className="detail-tabs-mobile-extra">{mobileExtra}</div>
        ) : null}
      </div>
      <Tabs
        {...rest}
        activeKey={currentKey}
        defaultActiveKey={defaultActiveKey}
        items={items}
        onChange={handleChange}
        renderTabBar={() => null}
        tabBarExtraContent={null}
        className="detail-tabs-mobile-content"
      >
        {children}
      </Tabs>
      <Drawer
        visible={drawerOpen}
        open={drawerOpen}
        placement="bottom"
        height="58vh"
        title={mobileTitle}
        className="detail-tabs-mobile-drawer"
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <div className="detail-tabs-mobile-list">
          {sections.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`detail-tabs-mobile-option${item.key === currentKey ? " detail-tabs-mobile-option-active" : ""}`}
              onClick={() => handleChange(item.key)}
            >
              <span className="detail-tabs-mobile-option-label">{item.tab || item.label}</span>
              {item.key === currentKey ? <span className="detail-tabs-mobile-option-selected">Selected</span> : null}
            </button>
          ))}
        </div>
      </Drawer>
    </div>
  );
};

DetailTabs.TabPane = TabPane;

export default DetailTabs;
